/**
 * Kits (booking_addons.is_bundle): a fixed-price add-on whose items are other
 * products, each either sized (the buyer picks a size) or single (included as
 * it is). Each kit sale is written as one line per item, carrying the chosen
 * product and its share of the kit price (public.allocate_bundle_price).
 * These helpers are pure and client-safe; the server re-runs them on catalog
 * rows it fetched itself, so a browser can only preview, never set, a price.
 */

import { z } from 'zod';

import {
  compareSizeLabels,
  getAddonBaseName,
} from './addon-size-groups.util';

/** Most items a kit may hold. */
export const MAX_BUNDLE_ITEMS = 10;

export interface BundleProductRow {
  id: string;
  name: string;
  price: number;
  variant_group_id?: string | null;
  variant_label?: string | null;
}

/** A kit item: a sized product (its size group) or a single product (its id). */
export interface BundleItemRow {
  variant_group_id: string | null;
  item_addon_id: string | null;
}

/**
 * One stored kit item (booking_addons.bundle_items element). The database
 * (kit.assert_bundle_items) checks the ids; this only reads the shape.
 */
export const BundleItemElementSchema = z.union([
  z.object({ variant_group_id: z.string().min(1) }).strict(),
  z.object({ item_addon_id: z.string().min(1) }).strict(),
]);

export type BundleItemElement = z.infer<typeof BundleItemElementSchema>;

/** A stored element, or an item row already read (the other id null). */
const ReadableItemSchema = z.union([
  z
    .object({ variant_group_id: z.string().min(1), item_addon_id: z.null().optional() })
    .strict(),
  z
    .object({ item_addon_id: z.string().min(1), variant_group_id: z.null().optional() })
    .strict(),
]);

const BundleItemsSchema = z.array(ReadableItemSchema).max(MAX_BUNDLE_ITEMS);

/**
 * A kit row's items as item rows, in kit order; [] when unreadable. Reads the
 * stored bundle_items or rows this already returned, so it can run twice.
 */
export function bundleItemRows(kit: { bundle_items?: unknown }): BundleItemRow[] {
  const parsed = BundleItemsSchema.safeParse(kit.bundle_items ?? []);

  if (!parsed.success) return [];

  return parsed.data.map((item) => ({
    variant_group_id: item.variant_group_id ?? null,
    item_addon_id: item.item_addon_id ?? null,
  }));
}

export type BundleOfferItem<T extends BundleProductRow> =
  | { kind: 'sized'; key: string; baseName: string; sizes: T[] }
  | { kind: 'single'; key: string; product: T };

export interface BundleOffer<K, T extends BundleProductRow> {
  kit: K;
  items: BundleOfferItem<T>[];
}

/**
 * Turn kit rows (with their bundle_items) and the tenant's product rows into
 * kits ready to display, items in their saved order and sizes sorted S → XXL.
 * A kit is left out when it has no items or when any item cannot be bought
 * (no size on offer, or a single product that is gone or now has sizes).
 */
export function buildBundleOffers<
  K extends { id: string; bundle_items?: unknown },
  T extends BundleProductRow,
>(kits: K[], products: T[]): BundleOffer<K, T>[] {
  const sizesByGroup = new Map<string, T[]>();
  const singles = new Map<string, T>();

  for (const product of products) {
    if (!product.variant_group_id) {
      singles.set(product.id, product);
      continue;
    }

    if (!product.variant_label) continue;

    const group = sizesByGroup.get(product.variant_group_id) ?? [];
    group.push(product);
    sizesByGroup.set(product.variant_group_id, group);
  }

  return kits.flatMap((kit) => {
    const kitItems = bundleItemRows(kit);

    if (kitItems.length === 0) return [];

    const offerItems: BundleOfferItem<T>[] = [];

    for (const item of kitItems) {
      if (item.item_addon_id) {
        const product = singles.get(item.item_addon_id);

        if (!product) return [];

        offerItems.push({ kind: 'single', key: product.id, product });
        continue;
      }

      const groupSizes = item.variant_group_id
        ? sizesByGroup.get(item.variant_group_id)
        : undefined;
      const sample = groupSizes?.[0];

      if (!item.variant_group_id || !groupSizes || !sample) return [];

      offerItems.push({
        kind: 'sized',
        key: item.variant_group_id,
        baseName: getAddonBaseName(sample.name, sample.variant_label),
        sizes: [...groupSizes].sort((a, b) =>
          compareSizeLabels(a.variant_label ?? '', b.variant_label ?? ''),
        ),
      });
    }

    return [{ kit, items: offerItems }];
  });
}

export type KitLineEntry<T> =
  | {
      kind: 'kit';
      saleId: string;
      /** The kit's name (bundle_name), else its first item's. */
      name: string;
      /** One name per kit line, in line order. */
      itemNames: string[];
      lines: T[];
      total: number;
    }
  | { kind: 'line'; row: T };

/**
 * Rows in their order, with each kit sale's lines folded into one entry at
 * the place of its first line, so a kit shows as a single add-on.
 */
export function foldKitLines<
  T extends {
    addon_name: string;
    bundle_name?: string | null;
    bundle_sale_id?: string | null;
    price_at_booking: number;
    quantity: number;
  },
>(rows: T[]): KitLineEntry<T>[] {
  const entries: KitLineEntry<T>[] = [];
  const kits = new Map<string, Extract<KitLineEntry<T>, { kind: 'kit' }>>();

  for (const row of rows) {
    if (!row.bundle_sale_id) {
      entries.push({ kind: 'line', row });
      continue;
    }

    let kit = kits.get(row.bundle_sale_id);

    if (!kit) {
      kit = {
        kind: 'kit',
        saleId: row.bundle_sale_id,
        name: row.bundle_name ?? row.addon_name,
        itemNames: [],
        lines: [],
        total: 0,
      };
      kits.set(row.bundle_sale_id, kit);
      entries.push(kit);
    }

    kit.lines.push(row);
    kit.itemNames.push(row.addon_name);
    kit.total = roundMoney(kit.total + Number(row.price_at_booking) * row.quantity);
  }

  return entries;
}

/** A sold product_order_items row, with the kit columns a kit sale sets. */
export interface SoldOrderLine {
  addon_id?: string | null;
  name_snapshot: string;
  quantity: number;
  bundle_sale_id?: string | null;
  bundle_addon_id?: string | null;
  bundle_name_snapshot?: string | null;
}

export type OrderKitEntry<T> =
  | { kind: 'line'; row: T }
  | {
      kind: 'kit';
      key: string;
      name: string;
      /** Kits sold: each kit sale holds every item once per kit. */
      quantity: number;
      /** One name per kit item, in the order the lines were read. */
      itemNames: string[];
      rows: T[];
    };

/**
 * Order lines with each kit folded into one entry at the place of its first
 * line. Kit sales of the same kit with the same products merge into one entry
 * with a quantity, so "2 × Kit" reads the same whether the order wrote one
 * sale per kit or one sale for both.
 */
export function foldOrderKitLines<T extends SoldOrderLine>(rows: T[]): OrderKitEntry<T>[] {
  const sales = new Map<string, T[]>();
  const sequence: Array<{ kind: 'line'; row: T } | { kind: 'sale'; saleId: string }> = [];

  for (const row of rows) {
    if (!row.bundle_sale_id) {
      sequence.push({ kind: 'line', row });
      continue;
    }

    const sale = sales.get(row.bundle_sale_id);

    if (sale) {
      sale.push(row);
      continue;
    }

    sales.set(row.bundle_sale_id, [row]);
    sequence.push({ kind: 'sale', saleId: row.bundle_sale_id });
  }

  const entries: OrderKitEntry<T>[] = [];
  const kits = new Map<string, Extract<OrderKitEntry<T>, { kind: 'kit' }>>();

  for (const step of sequence) {
    if (step.kind === 'line') {
      entries.push(step);
      continue;
    }

    const saleRows = sales.get(step.saleId) ?? [];
    const itemNames = new Map<string, string>();

    for (const row of saleRows) {
      const itemKey = row.addon_id ?? row.name_snapshot;
      if (!itemNames.has(itemKey)) itemNames.set(itemKey, row.name_snapshot);
    }

    const units = saleRows.reduce((sum, row) => sum + row.quantity, 0);
    const quantity = Math.max(1, Math.round(units / Math.max(1, itemNames.size)));
    const first = saleRows[0];
    const key = [first?.bundle_addon_id ?? '', ...[...itemNames.keys()].sort()].join(':');
    const existing = kits.get(key);

    if (existing) {
      existing.quantity += quantity;
      existing.rows.push(...saleRows);
      continue;
    }

    const names = [...itemNames.values()];
    const kit = {
      kind: 'kit' as const,
      key,
      name: first?.bundle_name_snapshot?.trim() || names.join(' + '),
      quantity,
      itemNames: names,
      rows: [...saleRows],
    };

    kits.set(key, kit);
    entries.push(kit);
  }

  return entries;
}

/** Stable key for one kit selection: the same kit with the same products. */
export function bundleSelectionKey(kitId: string, itemAddonIds: string[]): string {
  return [kitId, ...itemAddonIds].join(':');
}

export function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

/** A kit's price must leave at least one cent for every item. */
export const MIN_KIT_PRICE_PER_ITEM = 0.01;

/** True when the kit price covers at least a cent per item (cent-safe). */
export function kitPriceCoversItems(price: number, itemCount: number): boolean {
  return Math.round(price * 100) >= itemCount;
}
